import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { InlineSoldDateCell } from "@/components/inline-sold-date-cell";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "wouter";
import { AccessRestricted } from "@/components/access-restricted";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Car, DollarSign, TrendingDown, Clock, CheckCircle2, AlertTriangle,
  XCircle, ChevronUp, ChevronDown, Bell, Plus, Upload, Download, Info, X, Loader2,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface InventoryUnit {
  id: string;
  vin: string | null;
  stockNumber: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  lenderName: string | null;
  flooredAmount: string | null;
  purchasePrice: string | null;
  purchaseDate: string | null;
  fundingType: string | null;
  status: string;
  salePrice: string | null;
  saleDate: string | null;
  daysInStock: number | null;
  expectedSalePrice: string | null;
  alertSent5Days: boolean | null;
  alertSentTurnTime: boolean | null;
  dailyInterestCost: string | null;
  dailyFeeCost: string | null;
  daysOutstandingAtStmt: number | null;
  statementDate: string | null;
  // Computed by server
  currentDaysOnFloor: number;
  dailyHoldingCost: number;
  totalHoldingCostToDate: number;
  estimatedProfit: number | null;
  /** True when the projected carrying cost (days after statement) used the lender's True APR */
  aprProjected: boolean;
  /** The True APR (%) used for projection, if available */
  lenderTrueAprPct: number | null;
  /** True when cash vehicle holding cost uses the 18% default APR */
  cashAprIsDefault?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined, dec = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: dec });
}

function fmtNum(v: number | null | undefined, dec = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { maximumFractionDigits: dec });
}

// Format digits with commas as the user types (strips non-digits, adds commas)
function fmtInput(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return "";
  return parseInt(digits, 10).toLocaleString("en-US");
}

function getDaysColor(days: number, goalTurnDays: number | null): string {
  if (!goalTurnDays) return "text-foreground";
  const ratio = days / goalTurnDays;
  if (ratio >= 1) return "text-destructive font-bold";
  if (ratio >= 0.85) return "text-amber-600 dark:text-amber-400 font-semibold";
  if (ratio >= 0.65) return "text-yellow-600 dark:text-yellow-400";
  return "text-emerald-600 dark:text-emerald-400";
}

function getProfitColor(profit: number | null): string {
  if (profit == null) return "text-muted-foreground";
  if (profit > 0) return "text-emerald-600 dark:text-emerald-400";
  return "text-destructive";
}

// ─── Identity Text Cell (inline editor for make / model / year / vin) ─────────

function IdentityTextCell({
  unit,
  field,
  value,
  placeholder,
  monospace,
}: {
  unit: InventoryUnit;
  field: "make" | "model" | "year" | "vin";
  value: string | number | null;
  placeholder?: string;
  monospace?: boolean;
}) {
  const { t } = useTranslation(["inventory", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [optimisticValue, setOptimisticValue] = useState<string | number | null | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const suppressBlurRef = useRef(false);

  const displayValue = optimisticValue !== undefined ? optimisticValue : value;

  const saveMutation = useMutation({
    mutationFn: (patch: Record<string, any>) =>
      apiRequest("PATCH", `/api/inventory/${unit.id}/identity`, patch),
    onSuccess: (_data, patch) => {
      if (patch[field] !== undefined) setOptimisticValue(patch[field]);
      qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
      setEditing(false);
      toast({ title: t("inventory:fpi.toast.fieldUpdated", { field: t(`inventory:fields.${field}`) }) });
    },
    onError: (err: any) => {
      setOptimisticValue(undefined);
      toast({ title: t("inventory:fpi.toast.error"), description: err.message, variant: "destructive" });
    },
  });

  async function decodeVin(vin: string) {
    const v = vin.trim().toUpperCase();
    if (v.length !== 17) return;
    setDecoding(true);
    try {
      const resp = await fetch(`/api/vin-decode?vin=${encodeURIComponent(v)}`, { credentials: "include" });
      if (!resp.ok) { setDecoding(false); return; }
      const data = await resp.json();
      const patch: Record<string, any> = { vin: v };
      if (data.year) patch.year = parseInt(data.year, 10);
      if (data.make) patch.make = data.make;
      if (data.model) patch.model = data.model;
      saveMutation.mutate(patch);
    } catch {
      toast({ title: t("inventory:fpi.toast.vinDecodeFailed"), variant: "destructive" });
    } finally {
      setDecoding(false);
    }
  }

  function startEdit() {
    setInputVal(value != null ? String(value) : "");
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function confirm() {
    const trimmed = inputVal.trim();
    if (trimmed === "") {
      saveMutation.mutate({ [field]: null });
      return;
    }
    if (field === "year") {
      const y = parseInt(trimmed, 10);
      if (!Number.isFinite(y) || y < 1900 || y > 2100) {
        toast({ title: t("inventory:fpi.toast.invalidYear"), variant: "destructive" });
        return;
      }
      saveMutation.mutate({ year: y });
    } else if (field === "vin") {
      const cleaned = trimmed.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, "");
      if (cleaned.length !== 17) {
        toast({ title: t("inventory:fpi.toast.vinMust17"), variant: "destructive" });
        return;
      }
      saveMutation.mutate({ vin: cleaned });
    } else {
      saveMutation.mutate({ [field]: trimmed });
    }
  }

  if (editing) {
    const isVin = field === "vin";
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {saveMutation.isPending && <span className="text-[10px] text-muted-foreground animate-pulse">{t("inventory:fpi.identity.saving")}</span>}
        <input
          ref={inputRef}
          type={field === "year" ? "number" : "text"}
          value={inputVal}
          onChange={(e) => {
            const v = isVin
              ? e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, "").slice(0, 17)
              : e.target.value;
            setInputVal(v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { suppressBlurRef.current = true; confirm(); }
            if (e.key === "Escape") { suppressBlurRef.current = true; setEditing(false); }
          }}
          onBlur={() => {
            if (suppressBlurRef.current) {
              suppressBlurRef.current = false;
              return;
            }
            confirm();
          }}
          placeholder={placeholder}
          maxLength={isVin ? 17 : undefined}
          className={`text-xs border border-primary/40 rounded px-1.5 py-0.5 bg-background outline-none focus:ring-1 focus:ring-primary ${isVin ? "w-36 font-mono uppercase" : field === "year" ? "w-14 tabular-nums" : "w-24"}`}
          data-testid={`input-fp-identity-${field}-${unit.id}`}
          autoFocus
        />
        {isVin && (
          <button
            onMouseDown={() => { suppressBlurRef.current = true; }}
            onClick={() => { suppressBlurRef.current = false; decodeVin(inputVal); }}
            disabled={decoding || saveMutation.isPending || inputVal.replace(/[^A-HJ-NPR-Z0-9]/gi, "").length !== 17}
            className="text-[10px] px-1.5 py-0.5 rounded border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={t("inventory:fpi.identity.decodeTitle")}
            data-testid={`button-fp-vin-decode-${unit.id}`}
          >
            {decoding ? <Loader2 className="w-3 h-3 animate-spin" /> : t("inventory:fpi.identity.decode")}
          </button>
        )}
        <button
          onMouseDown={() => { suppressBlurRef.current = true; }}
          onClick={() => { suppressBlurRef.current = false; setEditing(false); }}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title={t("inventory:fpi.identity.cancel")}
          data-testid={`button-fp-cancel-identity-${field}-${unit.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); startEdit(); }}
      className={`text-xs hover:underline cursor-pointer hover:text-primary transition-colors text-left ${monospace ? "font-mono" : ""}`}
      title={t("inventory:fpi.identity.editTitle", { field })}
      data-testid={`cell-fp-identity-${field}-${unit.id}`}
    >
      {displayValue != null && displayValue !== "" ? String(displayValue) : <span className="text-muted-foreground/40">—</span>}
    </button>
  );
}

// ─── Sold Modal ───────────────────────────────────────────────────────────────

function SellModal({
  unit, open, onClose,
}: { unit: InventoryUnit | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation(["inventory", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [salePrice, setSalePrice] = useState("");

  const salePriceNum = parseFloat(salePrice.replace(/,/g, "")) || 0;

  const sellMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/floorplan-inventory/${unit!.id}/sell`, {
        salePrice: salePriceNum,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
      toast({ title: t("inventory:fpi.toast.unitMarkedSold") });
      setSalePrice("");
      onClose();
    },
    onError: (err: Error) => toast({ title: t("inventory:fpi.toast.error"), description: err.message, variant: "destructive" }),
  });

  const canSubmit = salePriceNum > 0;

  function handleClose() {
    setSalePrice("");
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("inventory:fpi.sell.title")}</DialogTitle>
          <DialogDescription>
            {t("inventory:fpi.sell.stockDescription", { label: unit?.stockNumber ? t("inventory:fpi.sell.stockLabel", { stock: unit.stockNumber }) : (unit?.vin ?? "") })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("inventory:fpi.sell.salePrice")}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
              <Input
                type="text" inputMode="numeric"
                placeholder={t("inventory:fpi.sell.salePricePlaceholder")}
                value={salePrice}
                onChange={(e) => setSalePrice(fmtInput(e.target.value))}
                className="pl-6"
                data-testid="input-sale-price"
              />
            </div>
          </div>
          {unit && canSubmit && (
            <div className="text-xs text-muted-foreground space-y-1 rounded-lg bg-muted/40 p-3">
              <div className="flex justify-between">
                <span>{t("inventory:fpi.sell.salePrice")}</span>
                <span className="font-medium">{fmt$(salePriceNum)}</span>
              </div>
              <div className="flex justify-between">
                <span>{unit.fundingType === "cash" ? t("inventory:fpi.sell.purchasePrice") : t("inventory:fpi.sell.flooredAmount")}</span>
                <span className="font-medium text-destructive/70">
                  − {fmt$(parseFloat(unit.fundingType === "cash" ? (unit.purchasePrice ?? "0") : (unit.flooredAmount ?? "0")))}
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t("inventory:fpi.sell.accruedCarryingCosts")}</span>
                <span className="font-medium text-destructive/70">− {fmt$(unit.totalHoldingCostToDate)}</span>
              </div>
              <Separator className="my-1" />
              <div className="flex justify-between font-semibold">
                <span>{t("inventory:fpi.sell.estNetProfit")}</span>
                {(() => {
                  const basis = parseFloat(unit.fundingType === "cash" ? (unit.purchasePrice ?? "0") : (unit.flooredAmount ?? "0"));
                  const profit = salePriceNum - basis - unit.totalHoldingCostToDate;
                  return <span className={getProfitColor(profit)}>{fmt$(profit)}</span>;
                })()}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="button-cancel-sell-dialog">{t("inventory:fpi.sell.cancel")}</Button>
          <Button
            onClick={() => sellMutation.mutate()}
            disabled={!canSubmit || sellMutation.isPending}
            data-testid="button-confirm-sell"
          >
            {sellMutation.isPending ? t("inventory:fpi.sell.saving") : t("inventory:fpi.sell.markAsSold")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Expected Selling Price Editor ────────────────────────────────────────────

function PriceEditor({
  unit,
}: { unit: InventoryUnit }) {
  const { t } = useTranslation(["inventory", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(
    unit.expectedSalePrice
      ? parseInt(unit.expectedSalePrice, 10).toLocaleString("en-US")
      : ""
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", `/api/floorplan-inventory/${unit.id}/price`, {
        expectedSalePrice: val ? parseFloat(val.replace(/,/g, "")) : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
      setEditing(false);
    },
    onError: (err: Error) => toast({ title: t("inventory:fpi.toast.failedToSave"), description: err.message, variant: "destructive" }),
  });

  if (!editing) {
    return (
      <button
        onClick={() => {
          setVal(unit.expectedSalePrice ? parseInt(unit.expectedSalePrice, 10).toLocaleString("en-US") : "");
          setEditing(true);
        }}
        className="text-sm tabular-nums hover:underline cursor-pointer text-left w-full"
        data-testid={`button-edit-price-${unit.id}`}
      >
        {unit.expectedSalePrice
          ? fmt$(parseFloat(unit.expectedSalePrice))
          : <span className="text-muted-foreground text-xs italic">{t("inventory:fpi.price.setPrice")}</span>}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
        <Input
          type="text" inputMode="numeric"
          value={val}
          onChange={(e) => setVal(fmtInput(e.target.value))}
          className="pl-5 h-7 w-32 text-xs"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") saveMutation.mutate();
            if (e.key === "Escape") setEditing(false);
          }}
          data-testid={`input-expected-price-${unit.id}`}
        />
      </div>
      <Button size="sm" className="h-7 px-2 text-xs" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save-expected-price">{t("inventory:fpi.price.save")}</Button>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(false)} data-testid="button-cancel-expected-price">✕</Button>
    </div>
  );
}

// ─── Sort helper ──────────────────────────────────────────────────────────────

type SortKey = "days" | "accrued" | "profit" | "floored" | "stock";
type SortDir = "asc" | "desc";

function SortButton({ label, sortKey, current, dir, onClick }: {
  label: string; sortKey: SortKey; current: SortKey; dir: SortDir; onClick: () => void;
}) {
  const active = current === sortKey;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-0.5 text-xs font-semibold whitespace-nowrap ${active ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
      data-testid={`button-sort-${sortKey}`}
    >
      {label}
      {active ? (dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />) : null}
    </button>
  );
}

// ─── Insights Card ────────────────────────────────────────────────────────────

function InsightsCard({ sold }: { sold: InventoryUnit[] }) {
  const { t } = useTranslation(["inventory", "common"]);
  if (!sold.length) return null;

  const avgTurnDays = sold.reduce((s, u) => s + (u.daysInStock ?? 0), 0) / sold.length;

  const byMonth = new Map<string, number>();
  for (const u of sold) {
    const m = u.saleDate ? u.saleDate.slice(0, 7) : "Unknown";
    byMonth.set(m, (byMonth.get(m) ?? 0) + 1);
  }
  const sortedMonths = Array.from(byMonth.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  const avgPerMonth = sold.length / Math.max(sortedMonths.length, 1);

  const totalRevenue = sold.reduce((s, u) => s + (u.salePrice ? parseFloat(u.salePrice) : 0), 0);
  const totalCost = sold.reduce((s, u) => {
    const basis = u.fundingType === "cash"
      ? parseFloat(u.purchasePrice ?? "0")
      : parseFloat(u.flooredAmount ?? "0");
    return s + basis + u.totalHoldingCostToDate;
  }, 0);
  const totalProfit = totalRevenue - totalCost;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="pt-4 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:fpi.insights.unitsSold")}</p>
          <p className="text-2xl font-bold tabular-nums">{sold.length}</p>
          <p className="text-xs text-muted-foreground">{t("inventory:fpi.insights.fromFloorplan")}</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:fpi.insights.avgTurnTime")}</p>
          <p className="text-2xl font-bold tabular-nums">{fmtNum(avgTurnDays, 0)}</p>
          <p className="text-xs text-muted-foreground">{t("inventory:fpi.insights.daysOnFloor")}</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:fpi.insights.avgPerMonth")}</p>
          <p className="text-2xl font-bold tabular-nums">{fmtNum(avgPerMonth, 1)}</p>
          <p className="text-xs text-muted-foreground">{t("inventory:fpi.insights.unitsSoldPerMonth")}</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:fpi.insights.totalProfit")}</p>
          <p className={`text-2xl font-bold tabular-nums ${getProfitColor(totalProfit)}`}>{fmt$(totalProfit)}</p>
          <p className="text-xs text-muted-foreground">{t("inventory:fpi.insights.afterCarrying")}</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Add Cash Vehicle Modal ───────────────────────────────────────────────────

function AddVehicleModal({ open, onClose, existingVins }: { open: boolean; onClose: () => void; existingVins: Set<string> }) {
  const { t } = useTranslation(["inventory", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [tab, setTab] = useState<"manual" | "csv">("manual");
  const fileRef = useRef<HTMLInputElement>(null);

  // Manual form state
  const [vin, setVin] = useState("");
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [stockNumber, setStockNumber] = useState("");
  const [isDuplicateVin, setIsDuplicateVin] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);

  // CSV state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvResult, setCsvResult] = useState<{ imported: number; skipped: number[] } | null>(null);

  const addMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/floorplan-inventory/cash", {
        vin: vin.trim() || undefined,
        year: year ? parseInt(year) : undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
        purchasePrice: parseFloat(purchasePrice.replace(/,/g, "")),
        purchaseDate,
        stockNumber: stockNumber.trim() || undefined,
      }).then((r) => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
      toast({ title: t("inventory:fpi.toast.cashVehicleAdded") });
      handleClose();
    },
    onError: (err: Error) => toast({ title: t("inventory:fpi.toast.error"), description: err.message, variant: "destructive" }),
  });

  const bulkMutation = useMutation({
    mutationFn: async (rows: Record<string, string>[]) => {
      const r = await apiRequest("POST", "/api/floorplan-inventory/cash/bulk", { rows });
      return r.json() as Promise<{ imported: number; skipped: number[] }>;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
      setCsvResult(data);
    },
    onError: (err: Error) => toast({ title: t("inventory:fpi.toast.importFailed"), description: err.message, variant: "destructive" }),
  });

  function handleClose() {
    setVin(""); setYear(""); setMake(""); setModel("");
    setPurchasePrice(""); setPurchaseDate(new Date().toISOString().slice(0, 10));
    setStockNumber(""); setCsvFile(null); setCsvResult(null);
    setIsDuplicateVin(false); setShowDuplicateConfirm(false);
    setTab("manual");
    onClose();
  }

  function handleCsvUpload(file: File) {
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.trim().split(/\r?\n/);
      if (lines.length < 2) {
        toast({ title: t("inventory:fpi.toast.csvEmpty"), variant: "destructive" });
        return;
      }
      const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
      const rows = lines.slice(1).map((line) => {
        const vals = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
        const row: Record<string, string> = {};
        headers.forEach((h, i) => { row[h] = vals[i] ?? ""; });
        return row;
      });
      bulkMutation.mutate(rows);
    };
    reader.readAsText(file);
  }

  function downloadTemplate() {
    const csv = "VIN,Year,Make,Model,Purchase Price,Purchase Date,Stock Number\n1HGCM82633A004352,2023,Honda,Accord,22000,2024-01-15,A001";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "cash_vehicle_template.csv";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  const canSubmitManual = purchasePrice && parseFloat(purchasePrice.replace(/,/g, "")) > 0 && purchaseDate;

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("inventory:fpi.cash.title")}</DialogTitle>
          <DialogDescription>
            {t("inventory:fpi.cash.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 border-b border-border pb-3 mb-4">
          <button
            onClick={() => setTab("manual")}
            className={`text-sm px-3 py-1.5 rounded-md transition-colors ${tab === "manual" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
            data-testid="tab-manual-entry"
          >
            {t("inventory:fpi.cash.manualEntry")}
          </button>
          <button
            onClick={() => setTab("csv")}
            className={`text-sm px-3 py-1.5 rounded-md transition-colors ${tab === "csv" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
            data-testid="tab-csv-import"
          >
            {t("inventory:fpi.cash.csvImport")}
          </button>
        </div>

        {tab === "manual" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2 sm:col-span-1">
                <Label className="text-xs">{t("inventory:fpi.cash.vinOptional")}</Label>
                <Input
                  value={vin}
                  onChange={(e) => {
                    const cleaned = e.target.value.trim().toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, "").slice(0, 17);
                    setVin(cleaned);
                    setIsDuplicateVin(cleaned.length === 17 && existingVins.has(cleaned));
                  }}
                  placeholder="1HGCM82633A004352"
                  className="h-8 text-sm font-mono"
                  data-testid="input-cash-vin"
                />
                {isDuplicateVin && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1" data-testid="warning-duplicate-vin-floor">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    {t("inventory:fpi.cash.duplicateVin")}
                  </p>
                )}
              </div>
              <div className="space-y-1.5 col-span-2 sm:col-span-1">
                <Label className="text-xs">{t("inventory:fpi.cash.stockOptional")}</Label>
                <Input
                  value={stockNumber} onChange={(e) => setStockNumber(e.target.value)}
                  placeholder="A001"
                  className="h-8 text-sm"
                  data-testid="input-cash-stock"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("inventory:fpi.cash.year")}</Label>
                <Input
                  type="number" value={year} onChange={(e) => setYear(e.target.value)}
                  placeholder="2023" className="h-8 text-sm"
                  data-testid="input-cash-year"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("inventory:fpi.cash.make")}</Label>
                <Input
                  value={make} onChange={(e) => setMake(e.target.value)}
                  placeholder="Honda" className="h-8 text-sm"
                  data-testid="input-cash-make"
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">{t("inventory:fpi.cash.model")}</Label>
                <Input
                  value={model} onChange={(e) => setModel(e.target.value)}
                  placeholder="Accord" className="h-8 text-sm"
                  data-testid="input-cash-model"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("inventory:fpi.cash.purchasePrice")} <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                  <Input
                    type="text" inputMode="numeric"
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(fmtInput(e.target.value))}
                    placeholder="22,000"
                    className="h-8 text-sm pl-5"
                    data-testid="input-cash-purchase-price"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("inventory:fpi.cash.purchaseDate")} <span className="text-destructive">*</span></Label>
                <Input
                  type="date" value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="input-cash-purchase-date"
                />
              </div>
            </div>
          </div>
        )}

        {tab === "csv" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{t("inventory:fpi.cash.csvInstructions")}</p>
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs shrink-0" onClick={downloadTemplate} data-testid="button-download-template">
                <Download className="w-3 h-3" /> {t("inventory:fpi.cash.template")}
              </Button>
            </div>

            <div
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()}
              data-testid="dropzone-csv"
            >
              <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
              {csvFile
                ? <p className="text-sm font-medium">{csvFile.name}</p>
                : <p className="text-sm text-muted-foreground">{t("inventory:fpi.cash.clickToUpload")}</p>}
              <input
                ref={fileRef} type="file" accept=".csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCsvUpload(f); }}
                data-testid="input-csv-file"
              />
            </div>

            {bulkMutation.isPending && (
              <p className="text-xs text-muted-foreground text-center">{t("inventory:fpi.cash.importingRows")}</p>
            )}

            {csvResult && (
              <div className="rounded-lg bg-muted/40 p-3 space-y-1 text-xs">
                <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {t("inventory:fpi.cash.vehiclesImported", { count: csvResult.imported })}
                </div>
                {csvResult.skipped.length > 0 && (
                  <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {t("inventory:fpi.cash.rowsSkipped", { count: csvResult.skipped.length, rows: csvResult.skipped.join(", ") })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="button-cancel-import-dialog">
            {csvResult ? t("inventory:fpi.cash.close") : t("inventory:fpi.cash.cancel")}
          </Button>
          {tab === "manual" && (
            <Button
              onClick={() => {
                if (isDuplicateVin) {
                  setShowDuplicateConfirm(true);
                } else {
                  addMutation.mutate();
                }
              }}
              disabled={!canSubmitManual || addMutation.isPending}
              data-testid="button-add-cash-vehicle"
            >
              {addMutation.isPending ? t("inventory:fpi.cash.adding") : t("inventory:fpi.cash.addVehicle")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showDuplicateConfirm} onOpenChange={(o) => { if (!o) setShowDuplicateConfirm(false); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("inventory:fpi.cash.duplicateTitle")}</DialogTitle>
          <DialogDescription>
            {t("inventory:fpi.cash.duplicateConfirm")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDuplicateConfirm(false)}>{t("inventory:fpi.cash.cancel")}</Button>
          <Button
            onClick={() => { setShowDuplicateConfirm(false); addMutation.mutate(); }}
            data-testid="button-confirm-duplicate-floor-vehicle"
          >
            {t("inventory:fpi.cash.addAnyway")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function FloorplanInventoryPage() {
  const { t } = useTranslation(["floorplan", "inventory", "common"]);
  const { data: authUser } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const { toast } = useToast();
  const qc = useQueryClient();
  const [sellUnit, setSellUnit] = useState<InventoryUnit | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("days");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [search, setSearch] = useState("");
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertNotifyByEmail, setAlertNotifyByEmail] = useState(false);
  const [alertCustomAgingDays, setAlertCustomAgingDays] = useState<string>("");

  // ── Scroll-to-unit from alert deep-link ──────────────────────────────────
  const locationSearch = useSearch();
  const targetUnitId = new URLSearchParams(locationSearch).get("unit");
  const [highlightedUnitId, setHighlightedUnitId] = useState<string | null>(targetUnitId);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  // Re-arm highlight whenever the ?unit= query param changes
  useEffect(() => {
    if (targetUnitId) setHighlightedUnitId(targetUnitId);
  }, [targetUnitId]);

  const { data: queryResult, isLoading } = useQuery<{ items: InventoryUnit[]; syncSummary: { added: number; updated: number } }>({
    queryKey: ["/api/floorplan-inventory"],
    queryFn: async () => (await apiRequest("POST", "/api/floorplan-inventory")).json(),
  });
  const items = queryResult?.items ?? [];

  // Scroll to and highlight the target unit once data is loaded
  useEffect(() => {
    if (!highlightedUnitId || isLoading) return;
    const el = rowRefs.current.get(highlightedUnitId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Remove highlight after animation
      const timer = setTimeout(() => setHighlightedUnitId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedUnitId, isLoading, items]);

  const { data: profile } = useQuery<Record<string, unknown>>({
    queryKey: ["/api/dealer-profile"],
  });

  const goalTurnDays = profile?.goalAvgTurnDays ? parseFloat(String(profile.goalAvgTurnDays)) : null;

  // Populate alert settings form whenever the dialog is opened
  useEffect(() => {
    if (showAlertSettings && profile) {
      setAlertEmail(String(profile.notifyEmail ?? ""));
      setAlertNotifyByEmail(Boolean(profile.notifyByEmail));
      setAlertCustomAgingDays(profile.alertCustomAgingDays != null ? String(profile.alertCustomAgingDays) : "");
    }
  }, [showAlertSettings, profile]);

  const saveAlertPrefsMutation = useMutation({
    mutationFn: (data: {
      notifyByEmail: boolean;
      notifyEmail: string;
      alertCustomAgingDays: number | null;
    }) => apiRequest("PATCH", "/api/alerts/preferences", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-profile"] });
      setShowAlertSettings(false);
      toast({ title: t("inventory:fpi.toast.alertPrefsSaved") });
    },
    onError: (err: any) => toast({ title: t("inventory:fpi.toast.failedToSave"), description: err.message, variant: "destructive" }),
  });

  function handleSaveAlertPrefs() {
    const parsedCustom = alertCustomAgingDays.trim() !== "" ? parseInt(alertCustomAgingDays, 10) : null;
    if (parsedCustom !== null && (isNaN(parsedCustom) || parsedCustom < 1)) {
      toast({ title: t("inventory:fpi.toast.invalidValue"), description: t("inventory:fpi.toast.customAgingMin"), variant: "destructive" });
      return;
    }
    saveAlertPrefsMutation.mutate({
      notifyByEmail: alertNotifyByEmail,
      notifyEmail: alertEmail,
      alertCustomAgingDays: parsedCustom,
    });
  }

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/floorplan-inventory/${id}/reactivate`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
      toast({ title: t("inventory:fpi.toast.unitReactivated") });
    },
    onError: (err: any) => toast({ title: t("inventory:fpi.toast.failedToReactivate"), description: err.message, variant: "destructive" }),
  });

  const active = useMemo(() => items.filter((i) => i.status === "active"), [items]);
  const sold = useMemo(() => items.filter((i) => i.status === "sold"), [items]);
  const activeVinSet = useMemo(() => new Set(active.map((i) => i.vin?.trim().toUpperCase()).filter((v): v is string => !!v)), [active]);

  const sortedActive = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = q
      ? active.filter((u) =>
        (u.vin ?? "").toLowerCase().includes(q) ||
        (u.stockNumber ?? "").toLowerCase().includes(q) ||
        (u.lenderName ?? "").toLowerCase().includes(q) ||
        (u.make ?? "").toLowerCase().includes(q) ||
        (u.model ?? "").toLowerCase().includes(q) ||
        (u.fundingType ?? "").toLowerCase().includes(q))
      : active;

    return [...filtered].sort((a, b) => {
      let diff = 0;
      if (sortKey === "days") diff = a.currentDaysOnFloor - b.currentDaysOnFloor;
      else if (sortKey === "accrued") diff = a.totalHoldingCostToDate - b.totalHoldingCostToDate;
      else if (sortKey === "profit") diff = (a.estimatedProfit ?? -Infinity) - (b.estimatedProfit ?? -Infinity);
      else if (sortKey === "floored") diff = parseFloat(a.flooredAmount ?? "0") - parseFloat(b.flooredAmount ?? "0");
      else if (sortKey === "stock") diff = (a.stockNumber ?? "").localeCompare(b.stockNumber ?? "");
      return sortDir === "desc" ? -diff : diff;
    });
  }, [active, sortKey, sortDir, search]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  }

  const alertCount = goalTurnDays
    ? active.filter((u) => u.currentDaysOnFloor >= goalTurnDays).length
    : 0;
  const warnCount = goalTurnDays
    ? active.filter((u) => u.currentDaysOnFloor >= goalTurnDays - 5 && u.currentDaysOnFloor < goalTurnDays).length
    : 0;

  const totalAccrued = active.reduce((s, u) => s + u.totalHoldingCostToDate, 0);

  if (authUser?.memberPermissions && !authUser.memberPermissions.inventoryAccess) {
    return <AccessRestricted feature={t("inventory:fpi.page.accessFeature")} />;
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-screen-2xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("inventory:fpi.page.title")}</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {t("inventory:fpi.page.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {alertCount > 0 && (
            <Badge variant="destructive" className="gap-1">
              <XCircle className="w-3 h-3" />{t("inventory:fpi.page.pastGoalTurn", { count: alertCount })}
            </Badge>
          )}
          {warnCount > 0 && (
            <Badge className="gap-1 bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
              <AlertTriangle className="w-3 h-3" />{t("inventory:fpi.page.approaching", { count: warnCount })}
            </Badge>
          )}
          <Button
            size="sm" variant="outline"
            className="gap-1.5"
            data-testid="button-alert-settings"
            onClick={() => setShowAlertSettings(true)}
          >
            <Bell className="w-3.5 h-3.5" />
            {t("inventory:fpi.page.alertSettings")}
          </Button>

          {/* Alert Settings Dialog */}
          <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t("inventory:fpi.alerts.title")}</DialogTitle>
                <DialogDescription>
                  {t("inventory:fpi.alerts.description")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-5 py-2">
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="alert-notify-email" className="flex flex-col gap-0.5">
                    <span>{t("inventory:fpi.alerts.emailNotifications")}</span>
                    <span className="text-xs font-normal text-muted-foreground">{t("inventory:fpi.alerts.emailNotificationsSub")}</span>
                  </Label>
                  <Switch
                    id="alert-notify-email"
                    data-testid="switch-alert-notify-email"
                    checked={alertNotifyByEmail}
                    onCheckedChange={setAlertNotifyByEmail}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="alert-email-input">{t("inventory:fpi.alerts.emailAddress")}</Label>
                  <Input
                    id="alert-email-input"
                    data-testid="input-alert-email"
                    type="email"
                    placeholder={t("inventory:fpi.alerts.emailPlaceholder")}
                    value={alertEmail}
                    onChange={(e) => setAlertEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="alert-custom-aging">
                    {t("inventory:fpi.alerts.customAging")} <span className="text-muted-foreground font-normal">{t("inventory:fpi.alerts.optional")}</span>
                  </Label>
                  <Input
                    id="alert-custom-aging"
                    data-testid="input-alert-custom-aging"
                    type="number"
                    min={1}
                    placeholder={t("inventory:fpi.alerts.customAgingPlaceholder")}
                    value={alertCustomAgingDays}
                    onChange={(e) => setAlertCustomAgingDays(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{t("inventory:fpi.alerts.customAgingHelp")}</p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  data-testid="button-alert-settings-cancel"
                  onClick={() => setShowAlertSettings(false)}
                >
                  {t("inventory:fpi.alerts.cancel")}
                </Button>
                <Button
                  data-testid="button-alert-settings-save"
                  onClick={handleSaveAlertPrefs}
                  disabled={saveAlertPrefsMutation.isPending}
                >
                  {saveAlertPrefsMutation.isPending ? t("inventory:fpi.alerts.saving") : t("inventory:fpi.alerts.save")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            size="sm"
            onClick={() => setShowAddVehicle(true)}
            className="gap-1.5"
            data-testid="button-add-vehicle"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("inventory:fpi.page.addVehicle")}
          </Button>
        </div>
      </div>

      {/* Summary cards — combined floored + cash */}
      {active.length > 0 && (() => {
        const flooredUnits = active.filter((u) => u.fundingType !== "cash");
        const cashUnits = active.filter((u) => u.fundingType === "cash");
        const totalCapital = active.reduce((s, u) => {
          const basis = u.fundingType === "cash"
            ? parseFloat(u.purchasePrice ?? "0")
            : parseFloat(u.flooredAmount ?? "0");
          return s + basis;
        }, 0);
        const flooredCapital = flooredUnits.reduce((s, u) => s + parseFloat(u.flooredAmount ?? "0"), 0);
        const cashCapital = cashUnits.reduce((s, u) => s + parseFloat(u.purchasePrice ?? "0"), 0);
        const totalDailyCost = active.reduce((s, u) => s + u.dailyHoldingCost, 0);
        const flooredDailyCost = flooredUnits.reduce((s, u) => s + u.dailyHoldingCost, 0);
        const cashDailyCost = cashUnits.reduce((s, u) => s + u.dailyHoldingCost, 0);
        const cashAprUnit = cashUnits.find((u) => u.lenderTrueAprPct != null);
        const cashAprPct = cashAprUnit?.lenderTrueAprPct ?? 18;
        const cashAprIsDefault = cashUnits.length === 0 ? true : (cashAprUnit?.cashAprIsDefault ?? true);
        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:fpi.summary.totalUnits")}</p>
                <p className="text-2xl font-bold tabular-nums" data-testid="stat-total-units">{active.length}</p>
                <p className="text-xs text-muted-foreground">{t("inventory:fpi.summary.flooredCashSplit", { floored: flooredUnits.length, cash: cashUnits.length })}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:fpi.summary.totalCommitted")}</p>
                <p className="text-2xl font-bold tabular-nums" data-testid="stat-total-capital">{fmt$(totalCapital)}</p>
                <p className="text-xs text-muted-foreground mt-1" data-testid="stat-floored-capital">{t("inventory:fpi.summary.flooredCapital", { amount: fmt$(flooredCapital), count: flooredUnits.length })}</p>
                <p className="text-xs text-muted-foreground" data-testid="stat-cash-capital">{t("inventory:fpi.summary.ownedCapital", { amount: fmt$(cashCapital), count: cashUnits.length })}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:fpi.summary.dailyCarryingCost")}</p>
                <p className="text-2xl font-bold tabular-nums text-destructive" data-testid="stat-daily-cost">{fmt$(totalDailyCost, 2)}</p>
                <p className="text-xs text-muted-foreground mt-1" data-testid="stat-floored-daily-cost">{t("inventory:fpi.summary.flooredDaily", { amount: fmt$(flooredDailyCost, 2) })}</p>
                <p className="text-xs text-muted-foreground" data-testid="stat-cash-daily-cost">{t("inventory:fpi.summary.ownedDaily", { amount: fmt$(cashDailyCost, 2), pct: cashAprPct.toFixed(2) })}</p>
                <p className="text-[10px] text-muted-foreground/70 italic mt-0.5" data-testid="stat-cash-apr-note">
                  {cashAprIsDefault
                    ? t("inventory:fpi.summary.aprDefaultNote")
                    : t("inventory:fpi.summary.aprNote", { pct: cashAprPct.toFixed(2) })}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t("inventory:fpi.summary.totalAccrued")}</p>
                <p className="text-2xl font-bold tabular-nums text-destructive" data-testid="stat-total-accrued">{fmt$(totalAccrued)}</p>
                <p className="text-xs text-muted-foreground">{t("inventory:fpi.summary.holdingCostsToDate")}</p>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Goal context */}
      {goalTurnDays && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
          <Clock className="w-3.5 h-3.5" />
          {t("inventory:fpi.goal.turnTime")} <strong className="text-foreground">{t("inventory:fpi.goal.days", { count: fmtNum(goalTurnDays, 0) })}</strong>
          <span className="text-xs text-muted-foreground">{t("inventory:fpi.goal.setIn")}</span>
        </div>
      )}

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active-inventory">
            {t("inventory:fpi.tabs.active", { count: active.length })}
          </TabsTrigger>
          <TabsTrigger value="sold" data-testid="tab-sold-inventory">
            {t("inventory:fpi.tabs.sold", { count: sold.length })}
          </TabsTrigger>
        </TabsList>

        {/* ── Active Inventory ── */}
        <TabsContent value="active" className="space-y-4 mt-4">
          {isLoading ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">{t("inventory:fpi.active.loading")}</CardContent></Card>
          ) : active.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Car className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
                <h3 className="font-semibold text-sm mb-1">{t("inventory:fpi.active.noActiveTitle")}</h3>
                <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                  {t("inventory:fpi.active.noActiveDescription")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Search + summary */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <Input
                  placeholder={t("inventory:fpi.active.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-xs h-8 text-sm"
                  data-testid="input-inventory-search"
                />
                <div className="flex gap-4 text-xs text-muted-foreground ml-auto">
                  <span>
                    {t("inventory:fpi.active.totalFloored")} <strong className="text-foreground">
                      {fmt$(active.reduce((s, u) => s + parseFloat(u.flooredAmount ?? "0"), 0))}
                    </strong>
                  </span>
                  <span>
                    {t("inventory:fpi.active.totalAccruedCosts")} <strong className="text-destructive">
                      {fmt$(totalAccrued)}
                    </strong>
                  </span>
                </div>
              </div>

              {/* Table */}
              <div className="rounded-xl border border-border overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left px-3 py-2.5 w-36">
                        <SortButton label={t("inventory:fpi.active.colStockVin")} sortKey="stock" current={sortKey} dir={sortDir} onClick={() => toggleSort("stock")} />
                      </th>
                      <th className="text-left px-3 py-2.5 hidden md:table-cell">
                        <span className="text-xs font-semibold text-muted-foreground">{t("inventory:fpi.active.colLender")}</span>
                      </th>
                      <th className="text-right px-3 py-2.5">
                        <SortButton label={t("inventory:fpi.active.colFloored")} sortKey="floored" current={sortKey} dir={sortDir} onClick={() => toggleSort("floored")} />
                      </th>
                      <th className="text-right px-3 py-2.5 w-20">
                        <SortButton label={t("inventory:fpi.active.colDays")} sortKey="days" current={sortKey} dir={sortDir} onClick={() => toggleSort("days")} />
                      </th>
                      <th className="text-right px-3 py-2.5 hidden sm:table-cell">
                        <SortButton label={t("inventory:fpi.active.colCarryingCost")} sortKey="accrued" current={sortKey} dir={sortDir} onClick={() => toggleSort("accrued")} />
                      </th>
                      <th className="text-right px-3 py-2.5">
                        <span className="text-xs font-semibold text-muted-foreground">{t("inventory:fpi.active.colAskingPrice")}</span>
                      </th>
                      <th className="text-right px-3 py-2.5">
                        <SortButton label={t("inventory:fpi.active.colEstProfit")} sortKey="profit" current={sortKey} dir={sortDir} onClick={() => toggleSort("profit")} />
                      </th>
                      <th className="px-3 py-2.5 w-16" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {sortedActive.map((unit) => {
                      const daysOverGoal = goalTurnDays ? unit.currentDaysOnFloor >= goalTurnDays : false;
                      const daysNearGoal = goalTurnDays
                        ? unit.currentDaysOnFloor >= goalTurnDays - 5 && !daysOverGoal
                        : false;
                      const isHighlighted = highlightedUnitId === unit.id;
                      return (
                        <tr
                          key={unit.id}
                          ref={(el) => {
                            if (el) rowRefs.current.set(unit.id, el);
                            else rowRefs.current.delete(unit.id);
                          }}
                          className={`transition-colors hover:bg-muted/20 ${isHighlighted ? "animate-pulse bg-primary/10 ring-2 ring-inset ring-primary/30" : daysOverGoal ? "bg-destructive/5" : daysNearGoal ? "bg-amber-500/5" : ""}`}
                          data-testid={`row-inventory-${unit.id}`}
                        >
                          {/* Stock / VIN */}
                          <td className="px-3 py-2.5">
                            <div>
                              {unit.stockNumber
                                ? <p className="text-xs font-semibold leading-tight">#{unit.stockNumber}</p>
                                : null}
                              <div className="flex items-center gap-1 flex-wrap mt-0.5" onClick={(e) => e.stopPropagation()}>
                                <IdentityTextCell unit={unit} field="year" value={unit.year} placeholder={t("inventory:fpi.identity.placeholderYear")} />
                                <IdentityTextCell unit={unit} field="make" value={unit.make} placeholder={t("inventory:fpi.identity.placeholderMake")} />
                                <IdentityTextCell unit={unit} field="model" value={unit.model} placeholder={t("inventory:fpi.identity.placeholderModel")} />
                              </div>
                              <div onClick={(e) => e.stopPropagation()}>
                                <IdentityTextCell unit={unit} field="vin" value={unit.vin} placeholder={t("inventory:fpi.identity.placeholderVin")} monospace />
                              </div>
                            </div>
                          </td>
                          {/* Lender / Funding type */}
                          <td className="px-3 py-2.5 hidden md:table-cell">
                            {unit.fundingType === "cash"
                              ? (
                                <div className="flex items-center gap-1.5">
                                  <Badge className="text-[10px] px-1.5 py-0 h-4 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 font-medium" data-testid={`badge-cash-${unit.id}`}>
                                    {t("inventory:fpi.active.cashBadge")}
                                  </Badge>
                                  {unit.cashAprIsDefault && (
                                    <span className="text-[10px] text-muted-foreground italic" title={t("inventory:fpi.active.defaultAprTitle")}>{t("inventory:fpi.active.defaultApr")}</span>
                                  )}
                                </div>
                              )
                              : <span className="text-xs text-muted-foreground">{unit.lenderName ?? "—"}</span>
                            }
                          </td>
                          {/* Floored / Purchase amount */}
                          <td className="px-3 py-2.5 text-right text-xs tabular-nums font-medium">
                            {unit.fundingType === "cash"
                              ? fmt$(parseFloat(unit.purchasePrice ?? "0"))
                              : fmt$(parseFloat(unit.flooredAmount ?? "0"))}
                          </td>
                          {/* Days on floor */}
                          <td className="px-3 py-2.5 text-right w-20">
                            <span className={`text-xs font-semibold tabular-nums ${getDaysColor(unit.currentDaysOnFloor, goalTurnDays)}`} data-testid={`text-days-${unit.id}`}>
                              {t("inventory:fpi.active.daysShort", { count: unit.currentDaysOnFloor })}
                            </span>
                          </td>
                          {/* Carrying cost */}
                          <td className="px-3 py-2.5 text-right text-xs text-destructive/80 tabular-nums hidden sm:table-cell" data-testid={`text-accrued-${unit.id}`}>
                            {fmt$(unit.totalHoldingCostToDate)}
                            {unit.aprProjected && unit.lenderTrueAprPct != null && (
                              <span
                                className="ml-1 text-[9px] text-primary/70 font-medium"
                                title={t("inventory:fpi.active.trueAprTitle", { pct: unit.lenderTrueAprPct.toFixed(4) })}
                                data-testid={`text-apr-projected-${unit.id}`}
                              >{t("inventory:fpi.active.trueApr")}</span>
                            )}
                          </td>
                          {/* Asking price — editable */}
                          <td className="px-3 py-2.5 text-right">
                            <PriceEditor unit={unit} />
                          </td>
                          {/* Est. profit */}
                          <td className={`px-3 py-2.5 text-right text-xs tabular-nums font-semibold ${getProfitColor(unit.estimatedProfit)}`} data-testid={`text-profit-${unit.id}`}>
                            {unit.estimatedProfit != null
                              ? fmt$(unit.estimatedProfit)
                              : <span className="text-muted-foreground font-normal">—</span>}
                          </td>
                          {/* Action */}
                          <td className="px-3 py-2.5 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-muted-foreground hover:text-emerald-600 hover:bg-emerald-500/10 gap-1 px-2"
                              onClick={() => setSellUnit(unit)}
                              data-testid={`button-sell-${unit.id}`}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">{t("inventory:fpi.active.soldButton")}</span>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Color legend */}
              {goalTurnDays && (
                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground px-1">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-destructive/20 inline-block" /> {t("inventory:fpi.active.legendPast")}</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500/20 inline-block" /> {t("inventory:fpi.active.legendNear")}</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/20 inline-block" /> {t("inventory:fpi.active.legendWithin")}</span>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ── Sold History ── */}
        <TabsContent value="sold" className="space-y-4 mt-4">
          {sold.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <DollarSign className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
                <h3 className="font-semibold text-sm mb-1">{t("inventory:fpi.sold.emptyTitle")}</h3>
                <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                  {t("inventory:fpi.sold.emptyDescription")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <InsightsCard sold={sold} />
              <div className="rounded-xl border border-border overflow-x-auto">
                <table className="w-full text-sm min-w-[680px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground w-36">{t("inventory:fpi.sold.colStockVin")}</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground hidden md:table-cell">{t("inventory:fpi.sold.colLender")}</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:fpi.sold.colFloored")}</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground w-16">{t("inventory:fpi.sold.colDays")}</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground hidden sm:table-cell">{t("inventory:fpi.sold.colCarryingCost")}</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:fpi.sold.colSalePrice")}</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:fpi.sold.colEstNetProfit")}</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-muted-foreground">{t("inventory:fpi.sold.colSoldOn")}</th>
                      <th className="px-3 py-2.5 w-14" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {[...sold].sort((a, b) => (b.saleDate ?? "").localeCompare(a.saleDate ?? "")).map((unit) => {
                      const salePriceN = unit.salePrice ? parseFloat(unit.salePrice) : null;
                      const isCash = unit.fundingType === "cash";
                      const basis = isCash ? parseFloat(unit.purchasePrice ?? "0") : parseFloat(unit.flooredAmount ?? "0");
                      const holdingCost = unit.totalHoldingCostToDate;
                      const netProfit = salePriceN != null ? salePriceN - basis - holdingCost : null;
                      return (
                        <tr key={unit.id} className="hover:bg-muted/20 transition-colors" data-testid={`row-sold-${unit.id}`}>
                          <td className="px-3 py-2.5">
                            <div>
                              {unit.stockNumber && <p className="text-xs font-semibold leading-tight">#{unit.stockNumber}</p>}
                              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                <IdentityTextCell unit={unit} field="year" value={unit.year} placeholder={t("inventory:fpi.identity.placeholderYear")} />
                                <IdentityTextCell unit={unit} field="make" value={unit.make} placeholder={t("inventory:fpi.identity.placeholderMake")} />
                                <IdentityTextCell unit={unit} field="model" value={unit.model} placeholder={t("inventory:fpi.identity.placeholderModel")} />
                              </div>
                              <IdentityTextCell unit={unit} field="vin" value={unit.vin} placeholder={t("inventory:fpi.identity.placeholderVin")} monospace />
                            </div>
                          </td>
                          <td className="px-3 py-2.5 hidden md:table-cell">
                            {isCash
                              ? <Badge className="text-[10px] px-1.5 py-0 h-4 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 font-medium">{t("inventory:fpi.sold.cashBadge")}</Badge>
                              : <span className="text-xs text-muted-foreground">{unit.lenderName ?? "—"}</span>
                            }
                          </td>
                          <td className="px-3 py-2.5 text-right text-xs tabular-nums font-medium">{fmt$(basis)}</td>
                          <td className="px-3 py-2.5 text-right text-xs tabular-nums">{unit.daysInStock ?? "—"}d</td>
                          <td className="px-3 py-2.5 text-right text-xs text-destructive/70 tabular-nums hidden sm:table-cell">{fmt$(holdingCost)}</td>
                          <td className="px-3 py-2.5 text-right text-xs font-semibold tabular-nums">{salePriceN != null ? fmt$(salePriceN) : "—"}</td>
                          <td className={`px-3 py-2.5 text-right text-xs tabular-nums font-semibold ${getProfitColor(netProfit)}`}>
                            {netProfit != null ? fmt$(netProfit) : "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right text-[11px] tabular-nums">
                            <InlineSoldDateCell
                              itemId={unit.id}
                              value={unit.saleDate}
                              onSaved={() => {}}
                              queryKeys={[["/api/floorplan-inventory"]]}
                              testIdPrefix="floorplan"
                              justify
                            />
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <Button
                              size="sm" variant="ghost" className="h-6 text-xs text-muted-foreground hover:text-foreground px-2"
                              onClick={() => reactivateMutation.mutate(unit.id)}
                              disabled={reactivateMutation.isPending}
                              data-testid={`button-reactivate-${unit.id}`}
                            >
                              {reactivateMutation.isPending ? "Reactivating…" : "Undo"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Monthly breakdown */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-primary" />
                    {t("inventory:floorplan.monthlyBreakdown")}
                  </CardTitle>
                  <CardDescription className="text-xs">{t("inventory:floorplan.monthlyBreakdownDesc")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(() => {
                      const byMonth = new Map<string, InventoryUnit[]>();
                      for (const u of sold) {
                        const m = u.saleDate ? u.saleDate.slice(0, 7) : "Unknown";
                        if (!byMonth.has(m)) byMonth.set(m, []);
                        byMonth.get(m)!.push(u);
                      }
                      return Array.from(byMonth.entries())
                        .sort((a, b) => b[0].localeCompare(a[0]))
                        .map(([month, units]) => {
                          const avgDays = units.reduce((s, u) => s + (u.daysInStock ?? 0), 0) / units.length;
                          const revenue = units.reduce((s, u) => s + (u.salePrice ? parseFloat(u.salePrice) : 0), 0);
                          return (
                            <div key={month} className="flex items-center gap-4 text-sm py-2 border-b border-border/40 last:border-0">
                              <span className="font-medium w-24 shrink-0">{month}</span>
                              <span className="text-muted-foreground">{units.length} unit{units.length !== 1 ? "s" : ""}</span>
                              <span className="text-muted-foreground">{t("inventory:floorplan.avgDays", { count: fmtNum(avgDays, 0) })}</span>
                              <span className="ml-auto font-semibold tabular-nums">{fmt$(revenue)}</span>
                            </div>
                          );
                        });
                    })()}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Sold modal */}
      <SellModal unit={sellUnit} open={sellUnit != null} onClose={() => setSellUnit(null)} />

      {/* Add vehicle modal */}
      <AddVehicleModal
        open={showAddVehicle}
        onClose={() => setShowAddVehicle(false)}
        existingVins={activeVinSet}
      />
    </div>
  );
}
